import url from 'url';

import qs from 'qs';

/**
 * Handles the difference between querystring and search/hash string.
 * The latter includes the question mark/pound sign which the querystring
 * module does not account for by default
 */
export function parseQueryString(queryString) {
  if (/^[?#]/.test(queryString)) {
    queryString = queryString.substr(1);
  }
  return qs.parse(queryString);
}

/**
 * Returns new querystring w/out overriding current params
 * leaves urls that came from server w/ params unchanged
 */
export function addToQuery(queryParams, originalQueryString) {
  return qs.stringify(
    {
      ...parseQueryString(originalQueryString),
      ...queryParams
    },
    {
      arrayFormat: 'brackets'
    }
  );
}

export function removeFromQuery(queryKeys, queryString) {
  const currentQueryParams = parseQueryString(queryString);
  [].concat(queryKeys).forEach(key => {
    delete currentQueryParams[key];
  });
  return qs.stringify(currentQueryParams, {
    arrayFormat: 'brackets'
  });
}

export function removeFromUrl(queryKeys, urlString) {
  const parsed = url.parse(urlString);
  return url.format({
    ...parsed,
    search: removeFromQuery(queryKeys, parsed.search || '')
  });
}

export function addPathFragment(
  { pathname, query, hash },
  originalPath = window.location.href
) {
  const parsed = url.parse(originalPath);
  return url.format({
    pathname: pathname || parsed.pathname,
    search: query || parsed.search,
    hash: hash || parsed.hash
  });
}

export function addQueryToUrl(queryParams, originalUrl) {
  const parsed = url.parse(originalUrl);
  return url.format({
    ...parsed,
    search: addToQuery(queryParams, parsed.search || '')
  });
}

/**
 * Splits a comma-separated list param (`?color=1,2`) into its values. The app
 * writes these lists as comma strings, but `qs` parses a repeated key
 * (`?color=1&color=2`, `?size[]=toy`) into an array, so arrays are flattened
 * too; a missing param or a nested object yields an empty list.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function splitParamList(value) {
  return []
    .concat(value ?? [])
    .flatMap(item => (typeof item === 'string' ? item.split(',') : []));
}

export function getSafeRedirectPath(queryString = window.location.search) {
  const { redirect_to: redirectTo } = parseQueryString(queryString);

  // Must be a same-origin path: a single leading '/'. A second '/' or a '\'
  // would make the browser treat it as a protocol-relative URL and redirect
  // off-site (e.g. `//evil.com`, `/\evil.com`).
  if (!(redirectTo && /^\/(?![/\\])/.test(redirectTo))) {
    return null;
  }

  return redirectTo;
}
